import { useTableActionContext } from '@aibees/elephant/context';
import { useLabelGroupingStatus } from '@aibees/elephant/hooks/labels/use-label-grouping-status';
import { useGetCreditsAmountTypeOnActionPopup } from '@aibees/elephant/hooks/use-get-credits-amount-type-on-action-popup';
import { ROUTES } from '@aibees/elephant/router';
import { LabelType } from '@aibees/elephant/types/label';
import { useMatchRoute } from '@aibees/shared/hooks';
import {
  DIALOG_IDS,
  getAccordionIdWithType,
  useTableAccordionStore,
} from '@aibees/shared/store/table-accordion-store';
import { useWorkspace } from '@aibees/shared/workspace';
import { useCallback, useEffect, useMemo } from 'react';

export function useAddLabelDialog() {
  // Context hooks
  const {
    handleAddToLabel,
    ids,
    labels,
    labelDialogState,
    handleClose,
    previousState,
    setPreviousState,
    effectiveSearchType,
  } = useTableActionContext();
  const isCompanyPage = useMatchRoute(ROUTES.searchCompany);
  // Use the flow's effective search type (folds in the ActionButton override) so a
  // newly created label matches the record's type, not just the page route.
  const grouping = useLabelGroupingStatus(effectiveSearchType);
  // Credit types
  const {
    aiCompany,
    aiPeople,
    aiUnsavedPeople,
    basicPeople,
    basicUnsavedPeople,
  } = useGetCreditsAmountTypeOnActionPopup();

  // Other hooks
  const workspace = useWorkspace();

  // Memoized values
  const showAiLookalike = useMemo(
    () =>
      aiCompany ||
      aiPeople ||
      aiUnsavedPeople ||
      basicPeople ||
      basicUnsavedPeople,
    [aiCompany, aiPeople, aiUnsavedPeople, basicPeople, basicUnsavedPeople]
  );

  // Use table accordion store directly
  const {
    getDialogExpanded,
    setDialogExpanded,
    setInitialExpanded,
    clearDialog,
  } = useTableAccordionStore();

  const dialogId = DIALOG_IDS.ADD_LABEL_DIALOG;
  const expandedAccordion = getDialogExpanded(dialogId);

  // Determine initial expanded accordion based on previousState
  const getInitialAccordionId = useCallback(() => {
    if (previousState === LabelType.LABEL) {
      return getAccordionIdWithType('LABEL');
    }
    return getAccordionIdWithType('LABEL');
  }, [previousState]);

  // Set initial expanded accordion when dialog opens
  useEffect(() => {
    if (labelDialogState.value) {
      setInitialExpanded(dialogId, getInitialAccordionId());
    }
  }, [
    labelDialogState.value,
    dialogId,
    setInitialExpanded,
    getInitialAccordionId,
  ]);

  const listDefaultValue = useMemo(
    () => (isCompanyPage ? 'Contact' : 'Account'),
    [isCompanyPage]
  );

  // Clear dialog state when dialog closes
  useEffect(() => {
    if (!labelDialogState.value) {
      clearDialog(dialogId);
    }
  }, [labelDialogState.value, clearDialog, dialogId]);

  // Handlers
  const setExpandedAccordion = useCallback(
    (accordionId: string | null) => {
      setDialogExpanded(dialogId, accordionId);
    },
    [dialogId, setDialogExpanded]
  );

  const handleDialogClose = useCallback(() => {
    handleClose('label');
    setPreviousState('');
  }, [handleClose, setPreviousState]);

  return {
    // State
    labelDialogState,
    ids,
    labels,
    expandedAccordion,
    showAiLookalike,
    workspace,
    grouping,
    setPreviousState,
    // Handlers
    handleAddToLabel,
    handleClose,
    handleDialogClose,
    setExpandedAccordion,

    // Computed values
    dialogId,
    hasIds: ids.length > 0,
    hasLabels: labels.length > 0,
    listDefaultValue,
  };
}
